

import { useState, useEffect } from "react";
import SearchableDropdown from "../components/SearchableDropdown";
import { mastersApi } from "@/lib/api";

interface EnquiryFormData {
  client: string;
  contact: string;
  email: string;
  type: string;
  guests: string;
  date: string;
  leadSource: string;
  status: string;
  notes: string;
}

interface EnquiryFormProps {
  onSubmit: (data: EnquiryFormData) => void;
  onCancel: () => void;
  initialData?: Partial<EnquiryFormData>;
  isEdit?: boolean;
}

const statuses = ["new", "qualified", "converted", "rejected"];

export default function EnquiryForm({ onSubmit, onCancel, initialData, isEdit }: EnquiryFormProps) {
  const [formData, setFormData] = useState<EnquiryFormData>({
    client: "",
    contact: "",
    email: "",
    type: "",
    guests: "",
    date: "",
    leadSource: "Walk-in",
    status: "new",
    notes: "",
  });

  // Helper to format date string to YYYY-MM-DD for input type="date"
  const formatDateForInput = (dateStr?: string) => {
    if (!dateStr) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "";
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    } catch {
      return "";
    }
  };

  // Reset form when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      setFormData({
        client: initialData.client || "",
        contact: initialData.contact || "",
        email: initialData.email || "",
        type: initialData.type || "",
        guests: initialData.guests ? initialData.guests.toString() : "",
        date: formatDateForInput(initialData.date),
        leadSource: initialData.leadSource || "",
        status: initialData.status || "new",
        notes: initialData.notes || "",
      });
    } else {
      // Reset to empty for add mode
      setFormData({
        client: "",
        contact: "",
        email: "",
        type: "",
        guests: "",
        date: "",
        leadSource: "",
        status: "new",
        notes: "",
      });
    }
  }, [initialData]);

  const [errors, setErrors] = useState<Partial<Record<keyof EnquiryFormData, string>>>({});
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [leadSources, setLeadSources] = useState(["Website", "Referral", "Social Media", "Walk-in", "Phone", "Email"]);

  useEffect(() => {
    mastersApi.eventTypes.list({ status: 'active' })
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setEventTypes(data.map((t: any) => t.name));
      })
      .catch(() => {
        setEventTypes(["Wedding", "Corporate", "Birthday", "Social", "Other"]);
      });
  }, []);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof EnquiryFormData, string>> = {};
    
    if (!formData.client.trim()) newErrors.client = "Client name is required";
    if (!formData.contact.trim()) newErrors.contact = "Contact number is required";
    if (!formData.type) newErrors.type = "Event type is required";
    if (!formData.date) newErrors.date = "Event date is required";
    if (!formData.leadSource) newErrors.leadSource = "Lead source is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const handleChange = (field: keyof EnquiryFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const inputClass = (field: keyof EnquiryFormData) => `
    w-full px-3 py-2.5 rounded-xl border text-sm transition-colors
    ${errors[field] 
      ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100" 
      : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
    }
    outline-none
  `;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex-1 space-y-4 overflow-y-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Client Name */}
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Client Name *</label>
            <input
              type="text"
              value={formData.client}
              onChange={(e) => handleChange("client", e.target.value)}
              placeholder="Enter client name"
              className={inputClass("client")}
            />
            {errors.client && <p className="text-xs text-rose-500 mt-1">{errors.client}</p>}
          </div>

          {/* Contact */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Contact Number *</label>
            <input
              type="tel"
              value={formData.contact}
              onChange={(e) => handleChange("contact", e.target.value)}
              placeholder="+91 98765 43210"
              className={inputClass("contact")}
            />
            {errors.contact && <p className="text-xs text-rose-500 mt-1">{errors.contact}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              placeholder="client@example.com"
              className={inputClass("email")}
            />
          </div>

          {/* Event Type */}
          <div>
            <SearchableDropdown
              label="Event Type *"
              options={eventTypes.map(t => ({ value: t, label: t }))}
              value={formData.type}
              onChange={(value) => handleChange("type", value)}
              placeholder="Select or type event type"
              allowAddNew
              onAddNew={(value) => {
                setEventTypes(prev => [...prev, value]);
                handleChange("type", value);
              }}
              error={errors.type}
            />
          </div>

          {/* Event Date */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Event Date *</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => handleChange("date", e.target.value)}
              className={inputClass("date")}
            />
            {errors.date && <p className="text-xs text-rose-500 mt-1">{errors.date}</p>}
          </div>

          {/* Guest Count */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Expected Guests</label>
            <input
              type="number"
              min="1"
              value={formData.guests}
              onChange={(e) => handleChange("guests", e.target.value)}
              placeholder="e.g., 150"
              className={inputClass("guests")}
            />
          </div>

          {/* Lead Source */}
          <div>
            <SearchableDropdown
              label="Lead Source *"
              options={leadSources.map(s => ({ value: s, label: s }))}
              value={formData.leadSource}
              onChange={(value) => handleChange("leadSource", value)}
              placeholder="Select or type lead source"
              allowAddNew
              onAddNew={(value) => {
                setLeadSources(prev => [...prev, value]);
                handleChange("leadSource", value);
              }}
              error={errors.leadSource}
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Status</label>
            <select
              value={formData.status}
              onChange={(e) => handleChange("status", e.target.value)}
              className={inputClass("status")}
            >
              {statuses.map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Additional details about the enquiry..."
              rows={3}
              className={`${inputClass("notes")} resize-none`}
            />
          </div>
        </div>
      </div>

      {/* Actions - Fixed at bottom */}
      <div className="flex gap-3 pt-4 border-t border-stone-100 mt-6 flex-shrink-0">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm bg-amber-500 hover:bg-amber-600 transition-colors"
        >
          {isEdit ? "Update Enquiry" : "Create Enquiry"}
        </button>
      </div>
    </form>
  );
}
